/**
 * Step 1 of 4 — Session details (CodeLive).
 *
 * Collects category, track/course/batch, subjects, schedule, trainer.
 * Streaming is always LiveKit. Topics for Live Class are chosen on Prepare Session (Step 2).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout, PageMain } from '../components/WizardShell';
import { MultiSelectDropdown } from '../components/schedule/MultiSelectDropdown';
import { SingleSelectDropdown } from '../components/schedule/SingleSelectDropdown';
import { DateTimeField } from '../components/schedule/DateTimeField';
import { WorkshopSessionFields } from '../components/schedule/WorkshopSessionFields';
import { ROUTES } from '../utils/createRoutes';
import {
  resizeWorkshopDayTopics,
  flattenWorkshopDayTopics,
  computeWorkshopSchedule,
  formatWorkshopTopicCovered,
  mergeDayTimings,
  buildDefaultDayTimings,
  type WorkshopDayTiming,
} from '../utils/workshopSessionUtils';
import { resolveLiveSessionName } from '../utils/liveSessionNaming';
import {
  LIVE_SESSION_CATEGORY_OPTIONS,
  OPTIONAL_SESSION_PURPOSES,
  type OptionalSessionPurpose,
} from '../config/liveSessionTypes';
import { isWorkshopCategory } from '../utils/sessionCategoryUtils';
import {
  clearAdminCreateSessionFlow,
  hasValidCreateDraft,
  loadCreateSessionDraft,
  saveCreateSessionDraft,
  seedPlannerTopicsFromDraft,
  shouldReseedPlannerFromCreate,
  startAdminCreateSessionFlow,
  type CreateSessionDraft,
} from '../utils/createSessionFlowStorage';
import {
  clearCurriculumPath,
  saveCurriculumPathForTimeline,
} from '../utils/curriculumPathStorage';
import { resetPlannerTopics } from '../utils/topicPlannerStorage';
import {
  fetchCourseLiveSessionPlan,
  filterPlanSessionsByCategory,
  findPlannedSessionByKey,
  isPlannedSessionUsedByTitles,
  plannedSessionDayTopicTitles,
  plannedSessionLabel,
  plannedSessionToCurriculumTopics,
  plannedSessionWorkshopDayCount,
  resolveTitleFromPlannedSession,
  type PlannedLiveSession,
} from '../utils/courseLiveSessionPlanSeed';
import { checkSessionNameAvailability } from '../utils/liveSessionNaming';
import type { SessionCategory } from '../utils/scheduledSessionStorage';
import { authService } from '../../../utils/authService';
import { fetchAllTracksForCourses } from '../../../utils/trackUtils';
import { fetchCoursesByTrack, fetchSubjectsOfCourses } from '../../../utils/courseUtils';
import { fetchBatchesByCourseSingle } from '../../../utils/batchUtils';
import {
  fetchSubjectsForTrack,
  fetchTopicsForSubject,
  type ContentSubject,
} from '../../../utils/contentUtils';
import {
  conflictSessionId,
  findSelfTrainerConflict,
  otherTrainerConflicts,
} from '../utils/trainerAvailability';

/** Hydrate Step 1 from in-progress wizard draft (back navigation). */
function readWizardDraft(): CreateSessionDraft | null {
  return hasValidCreateDraft() ? loadCreateSessionDraft() : null;
}

type TrackRow = { track_id: string; track_name: string };
type CourseRow = { course_id: string; course_name: string };
type BatchRow = { batch_id: string; batch_name: string; student_count?: number };
type TrainerRow = { trainer_id: string; trainer_name: string };

function toLocalDateIso(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toLocalTimeHm(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Start = now (local); end = start + 1 hour. */
function defaultSessionWindow() {
  const start = new Date();
  const end = new Date(start.getTime() + 60 * 60_000);
  return {
    startDate: toLocalDateIso(start),
    startTime: toLocalTimeHm(start),
    endDate: toLocalDateIso(end),
    endTime: toLocalTimeHm(end),
  };
}

export function CreateSessionAdmin() {
  const navigate = useNavigate();
  const [wizardDraft] = useState(() => readWizardDraft());
  const [defaults] = useState(() => defaultSessionWindow());
  const [sessionCategory, setSessionCategory] = useState<SessionCategory>(
    () => wizardDraft?.sessionCategory ?? 'mandatory',
  );
  const [tracks, setTracks] = useState<TrackRow[]>([]);
  const [courses, setCourses] = useState<CourseRow[]>([]);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [subjects, setSubjects] = useState<string[]>(
    () => wizardDraft?.subjects ?? [],
  );
  const [availableSubjects, setAvailableSubjects] = useState<string[]>([]);
  const [trackSubjects, setTrackSubjects] = useState<ContentSubject[]>([]);
  const [trackId, setTrackId] = useState(() => wizardDraft?.trackId ?? '');
  const [courseId, setCourseId] = useState(() => wizardDraft?.courseId ?? '');
  const [batchIds, setBatchIds] = useState<string[]>(() => {
    if (wizardDraft?.batchIds?.length) return wizardDraft.batchIds;
    if (wizardDraft?.batchId) return [wizardDraft.batchId];
    return [];
  });
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topicCovered, setTopicCovered] = useState(
    () => wizardDraft?.topicCovered ?? '',
  );
  const [topicOptions, setTopicOptions] = useState<string[]>([]);
  const [workshopDays, setWorkshopDays] = useState(
    () => Math.max(1, wizardDraft?.workshopDays || 1),
  );
  const [dayTopics, setDayTopics] = useState<string[][]>(() =>
    wizardDraft?.dayTopics?.length ? wizardDraft.dayTopics : [[]],
  );
  const [daySchedules, setDaySchedules] = useState<WorkshopDayTiming[]>(
    () => wizardDraft?.daySchedules ?? [],
  );
  const [deliveryTrainerId, setDeliveryTrainerId] = useState(
    () => wizardDraft?.deliveryTrainerId || wizardDraft?.trainerId || '',
  );
  const [startDate, setStartDate] = useState(
    () => wizardDraft?.startDate || defaults.startDate,
  );
  const [startTime, setStartTime] = useState(
    () => wizardDraft?.startTime || defaults.startTime,
  );
  const [endDate, setEndDate] = useState(
    () => wizardDraft?.endDate || defaults.endDate,
  );
  const [endTime, setEndTime] = useState(
    () => wizardDraft?.endTime || defaults.endTime,
  );
  const [trainerId, setTrainerId] = useState(
    () => wizardDraft?.trainerId ?? '',
  );
  const [sessionType, setSessionType] = useState<'group' | 'personal'>(
    () => wizardDraft?.sessionType ?? 'group',
  );
  const [optionalPurpose, setOptionalPurpose] = useState<OptionalSessionPurpose>(
    () => wizardDraft?.optionalPurpose ?? 'tracking',
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [planSessions, setPlanSessions] = useState<PlannedLiveSession[]>([]);
  const [planId, setPlanId] = useState<string | null>(
    () => wizardDraft?.courseLiveSessionPlanId ?? null,
  );
  const [planSessionKey, setPlanSessionKey] = useState(
    () => wizardDraft?.planSessionKey ?? '',
  );
  const [loadingPlan, setLoadingPlan] = useState(false);
  /** Existing LCM session titles for the selected batch (hide reused plan rows). */
  const [batchLcmTitles, setBatchLcmTitles] = useState<string[]>([]);
  /** All trainers (workshop / optional). LCM uses batchTrainers from trainer_batch. */
  const [allTrainers, setAllTrainers] = useState<TrainerRow[]>([]);
  const [batchTrainers, setBatchTrainers] = useState<TrainerRow[]>([]);
  const [loadingBatchTrainers, setLoadingBatchTrainers] = useState(false);

  const isLiveClass = sessionCategory === 'mandatory';
  const isWorkshop = isWorkshopCategory(sessionCategory);
  const isSupportSession = sessionCategory === 'optional';
  const usesCoursePlan = isLiveClass || isWorkshop;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [trackData, trainerRes] = await Promise.all([
          fetchAllTracksForCourses(),
          authService.get('/admin/get_all_trainer_names/'),
        ]);
        if (cancelled) return;
        setTracks(trackData || []);
        const trainerList: TrainerRow[] = (trainerRes?.data?.trainers || [])
          .map(
            (t: {
              user_id?: string;
              trainer_id?: string;
              id?: string;
              name?: string;
              trainer_name?: string;
            }) => ({
              trainer_id: String(t.user_id || t.trainer_id || t.id || ''),
              trainer_name: String(t.name || t.trainer_name || ''),
            }),
          )
          .filter((t: TrainerRow) => t.trainer_id && t.trainer_name);
        setAllTrainers(trainerList);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Failed to load tracks/trainers.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // LCM only: trainers from MySQL trainer_batch for the selected batch.
  useEffect(() => {
    if (!isLiveClass) {
      setBatchTrainers([]);
      setLoadingBatchTrainers(false);
      return;
    }
    const selectedBatchId = batchIds[0] || '';
    if (!selectedBatchId) {
      setBatchTrainers([]);
      setTrainerId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingBatchTrainers(true);
      try {
        const res = await authService.post(
          'admin/get_trainer_for_batch/',
          { batch_id: selectedBatchId },
          { headers: { 'Content-Type': 'application/json' } },
        );
        if (cancelled) return;
        const list: TrainerRow[] = (res?.data?.trainers || [])
          .filter(
            (t: { enabled?: boolean }) => t.enabled !== false,
          )
          .map(
            (t: {
              id?: string;
              trainer_id?: string;
              trainer_name?: string;
              name?: string;
            }) => ({
              trainer_id: String(t.id || t.trainer_id || ''),
              trainer_name: String(t.trainer_name || t.name || ''),
            }),
          )
          .filter((t: TrainerRow) => t.trainer_id && t.trainer_name);
        setBatchTrainers(list);
        setTrainerId((prev) =>
          list.some((t) => t.trainer_id === prev) ? prev : '',
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setBatchTrainers([]);
          setTrainerId('');
        }
      } finally {
        if (!cancelled) setLoadingBatchTrainers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLiveClass, batchIds]);

  useEffect(() => {
    if (!trackId) {
      setCourses([]);
      setCourseId('');
      setBatches([]);
      setBatchIds([]);
      setAvailableSubjects([]);
      setSubjects([]);
      setTrackSubjects([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [list, contentSubjects] = await Promise.all([
          fetchCoursesByTrack(trackId),
          fetchSubjectsForTrack(trackId, { excludeProject: true }),
        ]);
        if (cancelled) return;
        setTrackSubjects(contentSubjects || []);
        const nextCourses = (list || []).map((course) => ({
          course_id: String(course.course_id || '').trim(),
          course_name: String(course.course_name || '').trim(),
        })).filter((course) => course.course_id);
        setCourses(nextCourses);
        setCourseId((prev) => {
          const keep = nextCourses.some((course) => course.course_id === prev);
          if (!keep) {
            setBatches([]);
            setBatchIds([]);
            setAvailableSubjects([]);
            setSubjects([]);
          }
          return keep ? prev : '';
        });
      } catch (err) {
        console.error(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trackId]);

  useEffect(() => {
    if (!courseId) {
      setBatches([]);
      setBatchIds([]);
      setAvailableSubjects([]);
      setSubjects([]);
      setLoadingBatches(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingBatches(true);
      setBatches([]);
      try {
        const list = await fetchBatchesByCourseSingle(courseId);
        if (cancelled) return;
        const nextBatches = (list || []).map((batch) => ({
          batch_id: String(batch.batch_id || '').trim(),
          batch_name: String(batch.batch_name || '').trim(),
          student_count: Number(batch.students_count) || 0,
        })).filter((batch) => batch.batch_id);
        setBatches(nextBatches);
        // Restore draft batch selection when returning to Step 1.
        const draft = loadCreateSessionDraft();
        const draftIds =
          draft?.courseId === courseId
            ? draft.batchIds?.length
              ? draft.batchIds
              : draft.batchId
                ? [draft.batchId]
                : []
            : [];
        const validDraftIds = draftIds.filter((id) =>
          nextBatches.some((b) => b.batch_id === id),
        );
        setBatchIds((prev) => {
          const keepPrev = prev.filter((id) =>
            nextBatches.some((b) => b.batch_id === id),
          );
          if (validDraftIds.length) return validDraftIds;
          return keepPrev;
        });
        if (!nextBatches.length) {
          setError('This course has no batches yet. Add a batch before creating a session.');
        } else {
          setError((prev) =>
            prev && prev.toLowerCase().includes('batch') ? null : prev,
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setBatches([]);
          setError('Failed to load batches for this course.');
        }
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    // Subjects come from the course (Existing_Subjects), not per-batch mappings.
    if (!courseId) {
      setAvailableSubjects([]);
      setSubjects([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchSubjectsOfCourses([courseId]);
        if (cancelled) return;
        const list = Array.from(
          new Set(
            rows
              .map((s) => String(s.subject_name || '').trim())
              .filter(Boolean),
          ),
        );
        setAvailableSubjects(list);
        // Keep draft / current subject picks that are still valid.
        setSubjects((prev) => {
          const draft = loadCreateSessionDraft();
          const preferred =
            draft?.courseId === courseId && draft.subjects?.length
              ? draft.subjects
              : prev;
          const keep = preferred.filter((s) => list.includes(s));
          return keep;
        });
        setTopicCovered((prev) => {
          const draft = loadCreateSessionDraft();
          if (draft?.courseId === courseId && draft.topicCovered) {
            return draft.topicCovered;
          }
          return prev;
        });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setAvailableSubjects([]);
          setSubjects([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  useEffect(() => {
    if (!trackId || subjects.length === 0) {
      setTopicOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const matched = trackSubjects.filter((s) =>
          subjects.some(
            (name) => name.toLowerCase() === s.subject_name.toLowerCase(),
          ),
        );
        if (matched.length === 0) {
          setTopicOptions([]);
          return;
        }
        const topicLists = await Promise.all(
          matched.map((s) => fetchTopicsForSubject(s.subject_id)),
        );
        if (cancelled) return;
        const names = Array.from(
          new Set(
            topicLists
              .flat()
              .map((t) => String(t.topic_name || '').trim())
              .filter(Boolean),
          ),
        );
        setTopicOptions(names);
      } catch (err) {
        console.error(err);
        if (!cancelled) setTopicOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trackId, subjects, trackSubjects]);

  const filteredCourses = courses;
  const filteredBatches = batches.map((b) => ({
    id: String(b.batch_id),
    label: String(b.batch_name),
    studentCount: Number(b.student_count) || 0,
  }));
  const trainersForSelect = isLiveClass ? batchTrainers : allTrainers;
  const trainerOptions = trainersForSelect.map((t) => ({
    id: t.trainer_id,
    name: t.trainer_name,
    email: '',
    avatarUrl: '',
  }));

  const trainer = trainerOptions.find((t) => t.id === trainerId) ?? trainerOptions[0];
  const deliveryTrainer =
    trainerOptions.find((t) => t.id === deliveryTrainerId) ?? trainerOptions[0];

  const workshopSchedule = useMemo(() => {
    if (!isWorkshop) return null;
    return computeWorkshopSchedule(startDate, startTime, dayTopics, {
      dayTimings: daySchedules,
    });
  }, [isWorkshop, startDate, startTime, dayTopics, daySchedules]);

  const durationMinutes = useMemo(() => {
    if (isWorkshop) return workshopSchedule?.totalDurationMinutes ?? 0;
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 60;
    return Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
  }, [isWorkshop, workshopSchedule, startDate, startTime, endDate, endTime]);

  const workshopEndDate = workshopSchedule?.endDate ?? endDate;
  const workshopEndTime = workshopSchedule?.endTime ?? endTime;

  // Trainer availability (same API as Google Meet create / Step 4 schedule).
  const effectiveTrainerId = isWorkshop ? deliveryTrainerId : trainerId;
  const effectiveEndDate = isWorkshop ? workshopEndDate : endDate;
  const effectiveEndTime = isWorkshop ? workshopEndTime : endTime;
  const [autoSessionName, setAutoSessionName] = useState('');
  const [trainerAvailability, setTrainerAvailability] = useState<{
    status: 'idle' | 'checking' | 'available' | 'unavailable' | 'error';
    message: string;
    conflicts: Array<{
      session_title?: string;
      session_starttime?: string;
      session_expected_endtime?: string;
    }>;
  }>({ status: 'idle', message: '', conflicts: [] });

  useEffect(() => {
    if (
      !effectiveTrainerId ||
      !startDate ||
      !startTime ||
      !effectiveEndDate ||
      !effectiveEndTime
    ) {
      setTrainerAvailability({ status: 'idle', message: '', conflicts: [] });
      return;
    }
    let cancelled = false;
    setTrainerAvailability({ status: 'checking', message: '', conflicts: [] });
    const timer = setTimeout(async () => {
      try {
        // ---------------------------------------------------------------
        // step 1: Check trainer slot; ignore same-title self conflict
        // ---------------------------------------------------------------
        const response = await authService.post('/trainer/availability/', {
          trainer_id: effectiveTrainerId,
          start_date: startDate,
          end_date: effectiveEndDate,
          start_time: `${startTime}:00`,
          end_time: `${effectiveEndTime}:00`,
          session_id: '',
        });
        if (cancelled) return;
        const rawConflicts = response?.data?.list_of_sessions || [];
        const selfId = conflictSessionId(
          findSelfTrainerConflict(rawConflicts, autoSessionName),
        );
        let conflicts = otherTrainerConflicts(rawConflicts, autoSessionName);

        // Prefer server-side exclude when we know the existing session id.
        if (selfId && conflicts.length === 0 && response?.data?.success !== true) {
          const retry = await authService.post('/trainer/availability/', {
            trainer_id: effectiveTrainerId,
            start_date: startDate,
            end_date: effectiveEndDate,
            start_time: `${startTime}:00`,
            end_time: `${effectiveEndTime}:00`,
            session_id: selfId,
          });
          if (cancelled) return;
          if (retry?.data?.success === true) {
            setTrainerAvailability({
              status: 'available',
              message: 'Trainer is available for this time slot.',
              conflicts: [],
            });
            return;
          }
          conflicts = otherTrainerConflicts(
            retry?.data?.list_of_sessions || [],
            autoSessionName,
          );
        }

        if (response?.data?.success === true || conflicts.length === 0) {
          setTrainerAvailability({
            status: 'available',
            message: 'Trainer is available for this time slot.',
            conflicts: [],
          });
        } else {
          setTrainerAvailability({
            status: 'unavailable',
            message:
              response?.data?.message || 'Trainer is not available at this time.',
            conflicts,
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setTrainerAvailability({
            status: 'error',
            message: 'Could not verify trainer availability.',
            conflicts: [],
          });
        }
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    effectiveTrainerId,
    startDate,
    startTime,
    effectiveEndDate,
    effectiveEndTime,
    autoSessionName,
  ]);

  // All CodeLive types are saved unassigned — name sequence is course-scoped.
  const namingBatchId = '';
  const selectedCourseName =
    courses.find((c) => String(c.course_id) === String(courseId))?.course_name || '';
  const selectedSubjectIds = useMemo(
    () =>
      subjects
        .map(
          (name) =>
            trackSubjects.find(
              (s) => s.subject_name.toLowerCase() === name.toLowerCase(),
            )?.subject_id,
        )
        .map((id) => String(id || '').trim())
        .filter(Boolean),
    [subjects, trackSubjects],
  );

  /** LCM: hide planned sessions already applied to this batch. */
  const availablePlanSessions = useMemo(() => {
    if (!isLiveClass) return planSessions;
    return planSessions.filter(
      (s) =>
        !isPlannedSessionUsedByTitles(s, batchLcmTitles, selectedCourseName),
    );
  }, [isLiveClass, planSessions, batchLcmTitles, selectedCourseName]);

  useEffect(() => {
    if (!isLiveClass) {
      setBatchLcmTitles([]);
      return;
    }
    const batchId = batchIds[0] || '';
    if (!batchId || !courseId) {
      setBatchLcmTitles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const availability = await checkSessionNameAvailability({
          category: 'mandatory',
          subjectName: selectedSubjectIds[0] || subjects[0] || 'Session',
          startDate: startDate || new Date().toISOString().slice(0, 10),
          batchId,
          courseId,
          courseName: selectedCourseName,
          sessionProvider: 'livekit',
        });
        if (!cancelled) {
          setBatchLcmTitles(availability.existingTitles || []);
        }
      } catch {
        if (!cancelled) setBatchLcmTitles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isLiveClass,
    batchIds,
    courseId,
    selectedCourseName,
    selectedSubjectIds,
    subjects,
    startDate,
  ]);

  useEffect(() => {
    if (!isLiveClass) return;
    setPlanSessionKey((prev) => {
      if (prev && availablePlanSessions.some(
        (s) => String(s.session_key || s.sessionKey) === prev,
      )) {
        return prev;
      }
      if (availablePlanSessions.length === 1) {
        return String(
          availablePlanSessions[0].session_key ||
            availablePlanSessions[0].sessionKey ||
            '',
        );
      }
      return '';
    });
  }, [isLiveClass, availablePlanSessions]);

  useEffect(() => {
    let cancelled = false;
    // Middle title token is subject_id (not display name).
    const subjectIdForName = selectedSubjectIds[0] || 'Session';
    const selectedPlan = findPlannedSessionByKey(planSessions, planSessionKey);
    (async () => {
      if (usesCoursePlan && selectedPlan) {
        const fromPlan = resolveTitleFromPlannedSession(
          selectedPlan,
          selectedCourseName,
        );
        if (!cancelled && fromPlan) {
          setAutoSessionName(fromPlan);
          return;
        }
      }
      const name = await resolveLiveSessionName({
        category: sessionCategory,
        subjectName: subjectIdForName,
        startDate,
        batchId: namingBatchId,
        courseId,
        courseName: selectedCourseName,
        sessionProvider: 'livekit',
      });
      if (!cancelled) setAutoSessionName(name);
    })();
    return () => {
      cancelled = true;
    };
  }, [
    sessionCategory,
    selectedSubjectIds,
    startDate,
    namingBatchId,
    courseId,
    selectedCourseName,
    usesCoursePlan,
    planSessionKey,
    planSessions,
  ]);

  useEffect(() => {
    if (!usesCoursePlan || !courseId || !selectedSubjectIds[0]) {
      setPlanSessions([]);
      setPlanId(null);
      setPlanSessionKey('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPlan(true);
      try {
        const { planId: fetchedPlanId, sessions } =
          await fetchCourseLiveSessionPlan(courseId, selectedSubjectIds[0]);
        if (cancelled) return;
        const matching = filterPlanSessionsByCategory(sessions, sessionCategory);
        setPlanId(fetchedPlanId);
        setPlanSessions(matching);
        setPlanSessionKey((prev) => {
          if (prev && matching.some((s) => String(s.session_key || s.sessionKey) === prev)) {
            return prev;
          }
          if (matching.length === 1) {
            return String(matching[0].session_key || matching[0].sessionKey || '');
          }
          return '';
        });
      } finally {
        if (!cancelled) setLoadingPlan(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [usesCoursePlan, courseId, selectedSubjectIds, sessionCategory]);

  const handleWorkshopDaysChange = (days: number) => {
    setWorkshopDays(days);
    setDayTopics((prev) => resizeWorkshopDayTopics(days, prev));
    setDaySchedules((prev) =>
      mergeDayTimings(days, startDate, startTime, prev),
    );
  };

  const handleDayScheduleChange = (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => {
    setDaySchedules((prev) => {
      const base = mergeDayTimings(
        workshopDays,
        startDate,
        startTime,
        prev,
      );
      return base.map((day) =>
        day.dayIndex === dayIndex
          ? {
              ...day,
              startDate: patch.startDate ?? day.startDate,
              startTime: patch.startTime ?? day.startTime,
            }
          : day,
      );
    });
    if (dayIndex === 0) {
      if (patch.startDate) setStartDate(patch.startDate);
      if (patch.startTime) setStartTime(patch.startTime);
    }
  };

  const handleSubmit = () => {
    setError(null);
    if (!trackId) {
      setError('Select a track.');
      return;
    }
    if (!courseId) {
      setError('Select a course.');
      return;
    }
    // All CodeLive types are assigned later via Assign Sessions.
    if (batches.length === 0) {
      setError('This course has no batches yet. Add a batch before creating a session.');
      return;
    }
    if (isSupportSession && batchIds.length === 0) {
      setError('Select at least one batch for the optional session.');
      return;
    }
    if (isLiveClass && !batchIds[0]) {
      setError('Select a batch to list assigned trainers.');
      return;
    }
    if (isLiveClass && trainerOptions.length === 0) {
      setError(
        'No trainers are assigned to this batch. Assign trainers on the Trainer page first.',
      );
      return;
    }
    if (!subjects.length) {
      setError('Select at least one subject.');
      return;
    }
    if (!(trainerId || deliveryTrainerId)) {
      setError('Select a trainer.');
      return;
    }
    if (trainerAvailability.status === 'unavailable') {
      setError(
        trainerAvailability.message ||
          'Trainer is not available at the selected time. Pick a different time or trainer.',
      );
      return;
    }
    if (trainerAvailability.status === 'checking') {
      setError('Checking trainer availability — please wait a moment and try again.');
      return;
    }

    if (isWorkshop && !(usesCoursePlan && planSessions.length > 0 && planSessionKey)) {
      if (dayTopics.some((topics) => topics.length === 0)) {
        setError('Select at least one topic for each workshop day.');
        return;
      }
      const seenTopics = new Set<string>();
      for (const topics of dayTopics) {
        for (const topic of topics) {
          if (seenTopics.has(topic)) {
            setError(
              `Topic "${topic}" is assigned to more than one day. Each topic can only be used once.`,
            );
            return;
          }
          seenTopics.add(topic);
        }
      }
    }

    const selectedPlan =
      usesCoursePlan && planSessionKey
        ? findPlannedSessionByKey(planSessions, planSessionKey)
        : undefined;

    if (usesCoursePlan && planSessions.length > 0 && !selectedPlan) {
      if (isLiveClass && availablePlanSessions.length === 0) {
        setError(
          'Every planned LCM session for this subject is already applied to this batch.',
        );
        return;
      }
      setError('Select a planned session from the course live session plan.');
      return;
    }

    if (
      isLiveClass &&
      selectedPlan &&
      isPlannedSessionUsedByTitles(
        selectedPlan,
        batchLcmTitles,
        selectedCourseName,
      )
    ) {
      setError(
        'This batch already applied that planned session. Choose a different LCM planned session.',
      );
      return;
    }

    if (selectedPlan) {
      const curriculum = plannedSessionToCurriculumTopics(selectedPlan);
      if (curriculum.length === 0) {
        setError(
          'This planned session has no topics/subtopics. Update the course live session plan first.',
        );
        return;
      }
    }

    const selectedBatches = filteredBatches.filter((b) => batchIds.includes(b.id));
    const optionalBatchIds = isSupportSession ? batchIds : [];
    const primaryOptionalBatch = selectedBatches[0];

    const resolvedDaySchedules = isWorkshop
      ? mergeDayTimings(workshopDays, startDate, startTime, daySchedules)
      : [];

    const prevDraft = loadCreateSessionDraft();
    const fromCoursePlan = Boolean(selectedPlan);
    const planWorkshopDays = selectedPlan
      ? plannedSessionWorkshopDayCount(selectedPlan)
      : workshopDays;
    const planDayTopics = selectedPlan
      ? plannedSessionDayTopicTitles(selectedPlan)
      : dayTopics;
    const nextSelectedTopics = fromCoursePlan
      ? plannedSessionToCurriculumTopics(selectedPlan!).map((t) => t.title)
      : isWorkshop
        ? flattenWorkshopDayTopics(dayTopics)
        : selectedTopics;

    const draftPayload: CreateSessionDraft = {
      // Preserve Step 4 fields (QA / LiveKit toggles) across Create Continue.
      waitingRoomEnabled: prevDraft?.waitingRoomEnabled ?? false,
      trainerDefaultMic: prevDraft?.trainerDefaultMic ?? false,
      trainerDefaultCamera: prevDraft?.trainerDefaultCamera ?? false,
      studentDefaultMic: prevDraft?.studentDefaultMic ?? false,
      studentDefaultCamera: prevDraft?.studentDefaultCamera ?? false,
      qaDurationMinutes: prevDraft?.qaDurationMinutes,
      practiceTestEnabled: prevDraft?.practiceTestEnabled ?? false,
      sessionCategory,
      trackId,
      courseId,
      courseName: selectedCourseName,
      // Optional: persist selected batches. Other types stay unassigned until Assign Sessions.
      batchId: isSupportSession
        ? primaryOptionalBatch?.id || ''
        : isLiveClass
          ? batchIds[0] || ''
          : '',
      batchLabel: isSupportSession
        ? selectedBatches.map((b) => b.label).join(', ')
        : isLiveClass
          ? filteredBatches.find((b) => b.id === batchIds[0])?.label || ''
          : '',
      batchIds: isSupportSession
        ? optionalBatchIds
        : isLiveClass && batchIds[0]
          ? [batchIds[0]]
          : [],
      studentCount: isSupportSession
        ? selectedBatches.reduce((sum, b) => sum + (b.studentCount || 0), 0)
        : isLiveClass
          ? selectedBatches[0]?.studentCount || 0
          : 0,
      subjects,
      subjectIds: selectedSubjectIds,
      selectedTopics: nextSelectedTopics,
      topicCovered: isWorkshop
        ? formatWorkshopTopicCovered(fromCoursePlan ? planDayTopics : dayTopics)
        : topicCovered,
      workshopDays: fromCoursePlan && isWorkshop ? planWorkshopDays : workshopDays,
      dayTopics: fromCoursePlan && isWorkshop ? planDayTopics : dayTopics,
      daySchedules: resolvedDaySchedules,
      deliveryTrainerId,
      startDate,
      startTime,
      endDate: isWorkshop ? workshopEndDate : endDate,
      endTime: isWorkshop ? workshopEndTime : endTime,
      durationMinutes,
      trainerId: isWorkshop ? deliveryTrainerId : trainerId,
      trainerName: isWorkshop ? deliveryTrainer?.name || '' : trainer?.name || '',
      trainerAvatarUrl: '',
      sessionType,
      optionalPurpose,
      autoSessionName,
      sessionProvider: 'livekit',
      fromCoursePlan,
      planSessionKey: fromCoursePlan ? planSessionKey : undefined,
      planSessionTitle: fromCoursePlan
        ? String(selectedPlan?.title || '').trim() || undefined
        : undefined,
      courseLiveSessionPlanId: fromCoursePlan ? planId || undefined : undefined,
    };

    if (shouldReseedPlannerFromCreate(prevDraft, draftPayload)) {
      if (fromCoursePlan && selectedPlan) {
        clearCurriculumPath();
        resetPlannerTopics();
        saveCurriculumPathForTimeline(
          plannedSessionToCurriculumTopics(selectedPlan),
        );
      } else {
        clearCurriculumPath();
        seedPlannerTopicsFromDraft(nextSelectedTopics, {
          workshop: isWorkshop,
          dayTopics: isWorkshop ? dayTopics : undefined,
        });
      }
    }

    saveCreateSessionDraft(draftPayload);
    startAdminCreateSessionFlow();
    navigate(ROUTES.prepareSession);
  };

  return (
    <AppLayout>
      <PageMain>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {loading ? <p className="text-muted small mb-3">Loading courses...</p> : null}

          <div className="mb-3">
            <label className="form-label">Session Name (auto-generated)</label>
            <input
              type="text"
              className="form-control"
              value={autoSessionName}
              readOnly
              style={{ backgroundColor: '#e8f5e9' }}
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="form-label">Session Category</label>
              <SingleSelectDropdown
                value={sessionCategory}
                onChange={(value) => {
                  const next = value as SessionCategory;
                  if (!next) return;
                  setSessionCategory(next);
                  // Optional/LCO: default to personal so batches are scope only;
                  // students must be selected explicitly (not whole-batch invite).
                  if (next === 'optional') {
                    setSessionType('personal');
                  }
                  if (next === 'mandatory') {
                    setSubjects((prev) => [prev[0] ?? availableSubjects[0] ?? '']);
                  }
                  if (isWorkshopCategory(next)) {
                    setDayTopics((prev) =>
                      resizeWorkshopDayTopics(workshopDays, prev),
                    );
                    setDaySchedules(
                      buildDefaultDayTimings(startDate, startTime, workshopDays),
                    );
                  }
                }}
                options={LIVE_SESSION_CATEGORY_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                placeholder="Select category"
              />
            </div>
            {isSupportSession ? (
              <div className="col-md-6">
                <label className="form-label">Optional session type</label>
                <SingleSelectDropdown
                  value={optionalPurpose}
                  onChange={(value) => {
                    if (value) setOptionalPurpose(value as OptionalSessionPurpose);
                  }}
                  options={Object.entries(OPTIONAL_SESSION_PURPOSES).map(([value, purpose]) => ({
                    value,
                    label: purpose.label,
                  }))}
                  placeholder="Select optional type"
                />
              </div>
            ) : null}
          </div>

          {isSupportSession && (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Track</label>
                <SingleSelectDropdown
                  value={trackId}
                  onChange={setTrackId}
                  options={tracks.map((t) => ({
                    value: t.track_id,
                    label: t.track_name,
                  }))}
                  placeholder="Select track"
                  disabled={loading}
                />
              </div>
            </div>
          )}

          {!isSupportSession && (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Track</label>
                <SingleSelectDropdown
                  value={trackId}
                  onChange={setTrackId}
                  options={tracks.map((t) => ({
                    value: t.track_id,
                    label: t.track_name,
                  }))}
                  placeholder="Select track"
                  disabled={loading}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Course</label>
                <SingleSelectDropdown
                  value={courseId}
                  onChange={setCourseId}
                  options={filteredCourses.map((c) => ({
                    value: String(c.course_id),
                    label: String(c.course_name),
                  }))}
                  placeholder={trackId ? 'Select course' : 'Select track first'}
                  disabled={!trackId}
                />
              </div>
            </div>
          )}

          {isSupportSession && (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Course</label>
                <SingleSelectDropdown
                  value={courseId}
                  onChange={setCourseId}
                  options={filteredCourses.map((c) => ({
                    value: String(c.course_id),
                    label: String(c.course_name),
                  }))}
                  placeholder={trackId ? 'Select course' : 'Select track first'}
                  disabled={!trackId}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Batches (multi-select)</label>
                <MultiSelectDropdown
                  options={filteredBatches.map((b) => ({
                    value: b.id,
                    label: b.studentCount
                      ? `${b.label} (${b.studentCount})`
                      : b.label,
                  }))}
                  selected={batchIds}
                  onChange={setBatchIds}
                  placeholder={
                    !courseId
                      ? 'Select course first'
                      : loadingBatches
                        ? 'Loading batches...'
                        : filteredBatches.length
                          ? 'Select one or more batches'
                          : 'No batches for this course'
                  }
                  disabled={!courseId || loadingBatches || filteredBatches.length === 0}
                />
                <div className="form-text">
                  {sessionType === 'personal'
                    ? 'Batches define who you can pick from. Only students you select later will be invited.'
                    : 'Group invite will invite every student in the selected batches when the session is created.'}
                </div>
              </div>
            </div>
          )}

          {isSupportSession && (
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Subject (multi-select)</label>
                <MultiSelectDropdown
                  options={availableSubjects.map((s) => ({ value: s, label: s }))}
                  selected={subjects}
                  onChange={setSubjects}
                  placeholder={
                    !courseId
                      ? 'Select course first'
                      : batchIds.length === 0
                        ? 'Select batches first'
                        : availableSubjects.length
                          ? 'Select subjects'
                          : 'No subjects for selected batches'
                  }
                  disabled={!courseId || batchIds.length === 0 || availableSubjects.length === 0}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Topic Covered</label>
                <MultiSelectDropdown
                  options={topicOptions.map((t) => ({ value: t, label: t }))}
                  selected={selectedTopics}
                  onChange={(vals) => {
                    setSelectedTopics(vals);
                    setTopicCovered(vals.join(', '));
                  }}
                  placeholder="Select topics"
                  disabled={topicOptions.length === 0}
                />
              </div>
            </div>
          )}

          {!isSupportSession && !isWorkshop && (
            <>
              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Subject</label>
                  <SingleSelectDropdown
                    value={subjects[0] ?? ''}
                    onChange={(value) => setSubjects(value ? [value] : [])}
                    options={availableSubjects.map((s) => ({ value: s, label: s }))}
                    placeholder={
                      !courseId
                        ? 'Select course first'
                        : batches.length === 0
                          ? 'No batches for this course'
                          : availableSubjects.length
                            ? 'Select subject'
                            : 'No subjects mapped to this course'
                    }
                    disabled={!courseId || availableSubjects.length === 0}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Batch</label>
                  <SingleSelectDropdown
                    value={batchIds[0] || ''}
                    onChange={(value) => setBatchIds(value ? [value] : [])}
                    options={filteredBatches.map((b) => ({
                      value: b.id,
                      label: b.studentCount
                        ? `${b.label} (${b.studentCount})`
                        : b.label,
                    }))}
                    placeholder={
                      !courseId
                        ? 'Select course first'
                        : loadingBatches
                          ? 'Loading batches...'
                          : filteredBatches.length
                            ? 'Select batch'
                            : 'No batches for this course'
                    }
                    disabled={
                      !courseId || loadingBatches || filteredBatches.length === 0
                    }
                  />
                </div>
              </div>

              {usesCoursePlan && subjects.length > 0 && (
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Planned session (from course plan)
                    </label>
                    {loadingPlan ? (
                      <p className="small text-muted mb-0">Loading plan…</p>
                    ) : planSessions.length > 0 ? (
                      <SingleSelectDropdown
                        value={planSessionKey}
                        onChange={setPlanSessionKey}
                        options={availablePlanSessions.map((s) => ({
                          value: String(s.session_key || s.sessionKey || ''),
                          label: plannedSessionLabel(s),
                        }))}
                        placeholder={
                          !batchIds[0]
                            ? 'Select batch first'
                            : availablePlanSessions.length === 0
                              ? 'No planned sessions left for this batch'
                              : 'Select planned session'
                        }
                        disabled={
                          !batchIds[0] || availablePlanSessions.length === 0
                        }
                      />
                    ) : (
                      <p className="small text-muted mb-0">
                        No LCM sessions in the course plan for this subject.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {!isLiveClass && isWorkshop && (
            <div className="row g-3 mb-3">
              <div className="col-12">
                <div className="form-text mb-0">
                  Batch and students are assigned later on Assign Sessions.
                </div>
              </div>
            </div>
          )}

          {isWorkshop ? (
            <>
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <label className="form-label">Subject</label>
                  <SingleSelectDropdown
                    value={subjects[0] || ''}
                    onChange={(value) => setSubjects(value ? [value] : [])}
                    options={availableSubjects.map((s) => ({ value: s, label: s }))}
                    placeholder={
                      availableSubjects.length ? 'Select subject' : 'No subjects for this batch'
                    }
                    disabled={!courseId || availableSubjects.length === 0}
                  />
                  {usesCoursePlan && subjects.length > 0 && (
                    <div className="mt-3">
                      <label className="form-label">
                        Planned workshop (from course plan)
                      </label>
                      {loadingPlan ? (
                        <p className="small text-muted mb-0">Loading plan…</p>
                      ) : planSessions.length > 0 ? (
                        <SingleSelectDropdown
                          value={planSessionKey}
                          onChange={setPlanSessionKey}
                          options={planSessions.map((s) => ({
                            value: String(s.session_key || s.sessionKey || ''),
                            label: plannedSessionLabel(s),
                          }))}
                          placeholder="Select planned workshop"
                        />
                      ) : (
                        <p className="small text-muted mb-0">
                          No workshop in the course plan — assign day topics below.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {planSessions.length > 0 && planSessionKey ? (
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <DateTimeField
                      label="Workshop start date / time (Day 1)"
                      date={startDate}
                      time={startTime}
                      onDateChange={(value) => {
                        setStartDate(value);
                        const days =
                          plannedSessionWorkshopDayCount(
                            findPlannedSessionByKey(planSessions, planSessionKey)!,
                          ) || workshopDays;
                        setWorkshopDays(days);
                        setDaySchedules(
                          buildDefaultDayTimings(value, startTime, days),
                        );
                      }}
                      onTimeChange={(value) => {
                        setStartTime(value);
                        const days =
                          plannedSessionWorkshopDayCount(
                            findPlannedSessionByKey(planSessions, planSessionKey)!,
                          ) || workshopDays;
                        setDaySchedules(
                          buildDefaultDayTimings(startDate, value, days),
                        );
                      }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Delivery trainer</label>
                    <SingleSelectDropdown
                      value={deliveryTrainerId}
                      onChange={setDeliveryTrainerId}
                      options={trainerOptions.map((t) => ({
                        value: t.id,
                        label: t.name,
                      }))}
                      placeholder="Select trainer"
                    />
                  </div>
                </div>
              ) : (
              <WorkshopSessionFields
                workshopDays={workshopDays}
                onWorkshopDaysChange={handleWorkshopDaysChange}
                dayTopics={dayTopics}
                onDayTopicsChange={(dayIndex, topics) =>
                  setDayTopics((prev) =>
                    prev.map((value, index) => (index === dayIndex ? topics : value)),
                  )
                }
                topicOptions={topicOptions}
                daySchedules={daySchedules}
                onDayScheduleChange={handleDayScheduleChange}
                deliveryTrainerId={deliveryTrainerId}
                onDeliveryTrainerChange={setDeliveryTrainerId}
                deliveryTrainerOptions={trainerOptions}
                startDate={startDate}
                startTime={startTime}
                onStartDateChange={(value) => {
                  setStartDate(value);
                  setDaySchedules(
                    buildDefaultDayTimings(value, startTime, workshopDays),
                  );
                }}
                onStartTimeChange={(value) => {
                  setStartTime(value);
                  setDaySchedules(
                    buildDefaultDayTimings(startDate, value, workshopDays),
                  );
                }}
              />
              )}
            </>
          ) : (
            <>
              <div className="row g-3 mb-3 align-items-start">
                <div className="col-md-6">
                  <DateTimeField
                    label="Start Date / Time"
                    date={startDate}
                    time={startTime}
                    onDateChange={setStartDate}
                    onTimeChange={setStartTime}
                  />
                </div>
                <div className="col-md-6">
                  <DateTimeField
                    label="End Date / Time"
                    date={endDate}
                    time={endTime}
                    onDateChange={setEndDate}
                    onTimeChange={setEndTime}
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Trainer</label>
                  <SingleSelectDropdown
                    value={trainerId}
                    onChange={setTrainerId}
                    options={trainerOptions.map((t) => ({ value: t.id, label: t.name }))}
                    placeholder={
                      !batchIds[0]
                        ? 'Select batch first'
                        : loadingBatchTrainers
                          ? 'Loading trainers...'
                          : trainerOptions.length
                            ? 'Select trainer'
                            : 'No trainers assigned to this batch'
                    }
                    disabled={
                      !batchIds[0] ||
                      loadingBatchTrainers ||
                      trainerOptions.length === 0
                    }
                  />
                  {loadingBatchTrainers && (
                    <div className="form-text">Loading batch trainers…</div>
                  )}
                  {!loadingBatchTrainers &&
                    batchIds[0] &&
                    trainerOptions.length === 0 && (
                      <div className="form-text text-warning">
                        No enabled trainers assigned to this batch in
                        trainer_batch.
                      </div>
                    )}
                  {trainerAvailability.status === 'checking' && (
                    <div className="form-text">Checking trainer availability…</div>
                  )}
                  {trainerAvailability.status === 'available' && (
                    <div className="form-text text-success">
                      {trainerAvailability.message}
                    </div>
                  )}
                  {trainerAvailability.status === 'unavailable' && (
                    <div className="form-text text-danger">
                      {trainerAvailability.message}
                      {trainerAvailability.conflicts.length > 0 && (
                        <ul className="mb-0 ps-3">
                          {trainerAvailability.conflicts.slice(0, 3).map((s, i) => (
                            <li key={i}>
                              {s.session_title || 'Session'}
                              {s.session_starttime
                                ? ` — ${new Date(s.session_starttime).toLocaleString()}`
                                : ''}
                              {s.session_expected_endtime
                                ? ` to ${new Date(s.session_expected_endtime).toLocaleTimeString()}`
                                : ''}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {trainerAvailability.status === 'error' && (
                    <div className="form-text text-warning">
                      {trainerAvailability.message}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Invite Type</label>
                  <SingleSelectDropdown
                    value={sessionType}
                    onChange={(value) => {
                      if (value === 'group' || value === 'personal') setSessionType(value);
                    }}
                    options={[
                      { value: 'group', label: 'Group (whole batch)' },
                      { value: 'personal', label: 'Personal (select students)' },
                    ]}
                    placeholder="Select invite type"
                  />
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="codelive-wizard-actions">
            <button type="submit" className="btn btn-primary">
              Continue to Prepare Session
            </button>
            <Link
              to={ROUTES.viewSessions}
              className="btn btn-outline-secondary"
              onClick={() => clearAdminCreateSessionFlow()}
            >
              Cancel
            </Link>
          </div>
        </form>
      </PageMain>
    </AppLayout>
  );
}

export default CreateSessionAdmin;
